import { useQuery } from '@apollo/client';
import { Box } from 'braid-design-system';
import { useSearchParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { PaymentContainer } from 'src/components/PaymentContainer/PaymentContainer';
import { PaymentGenericError } from 'src/components/PaymentGenericError/PaymentGenericError';
import { GetCheckoutPaymentSuccessPageDocument } from 'src/generated/graphql';
import useOverflowScroll from 'src/hooks/useOverflowScroll';
import { isNonNullable } from 'src/utils';
import { validateListingProducts } from 'src/utils/listingProducts';

import { PaymentSuccessSingleProduct } from './components/PaymentSuccessSingleProduct';
import { PaymentSuccessWithSecondProduct } from './components/PaymentSuccessWithSecondProduct';

export const PaymentSuccessPage = () => {
  useOverflowScroll();
  const [searchParams] = useSearchParams();
  const productTokens = searchParams.getAll('productToken');
  const listingId = searchParams.get('listingId');

  const { data, error, loading } = useQuery(
    GetCheckoutPaymentSuccessPageDocument,
  );

  if (loading) {
    return (
      <PaymentContainer width="medium">
        <Box data={{ testid: 'payment-success-loader' }}>
          <CustomLoader />
        </Box>
      </PaymentContainer>
    );
  }

  // TODO unit test
  if (!data || error) {
    return <PaymentGenericError testId="basic-data-fetching-failure-error" />;
  }

  const client = data.user?.userView?.client;
  const validatedProducts = validateListingProducts(data.listingProducts);

  // TODO unit test
  if (!client) {
    return <PaymentGenericError testId="client-validation-error" />;
  }

  // TODO unit test
  if (validatedProducts.kind === 'error') {
    return <PaymentGenericError testId="product-validation-error" />;
  }

  const selectedProductsFromUrl = productTokens
    .map((token) =>
      data.listingProducts.find((product) => product.productToken === token),
    )
    .filter(isNonNullable);

  // Arrays have no fixed size so we cannot make guarantees about the items at
  // certain indices. Pulling them out allows us to do this
  const firstUrlProduct = selectedProductsFromUrl.at(0);
  const secondUrlProduct = selectedProductsFromUrl.at(1);

  // TODO unit test
  // We only support one or two products in the checkout flow
  if (!firstUrlProduct || selectedProductsFromUrl.length > 2) {
    return <PaymentGenericError testId="product-quantities-error" />;
  }

  if (secondUrlProduct) {
    return (
      <PaymentSuccessWithSecondProduct
        clientId={client.id}
        firstUrlProduct={firstUrlProduct}
        secondUrlProduct={secondUrlProduct}
        listingId={listingId}
      />
    );
  }

  return (
    <PaymentSuccessSingleProduct
      clientId={client.id}
      firstUrlProduct={firstUrlProduct}
      listingId={listingId}
    />
  );
};
